import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, Image, Box, Loader2, CheckCircle, XCircle, Download, RotateCcw,
  Eye, ChevronDown, ChevronUp, Trash2, FileCode2,
} from 'lucide-react';
import { clsx } from 'clsx';

// Always hit the deployed AI proxy directly. CORS on the proxy already
// allows localhost, so we don't need a dev-vs-prod switch.
//
// ⚠️  This is the PUBLIC demo path ("Try It Now" on the landing page).
// It maps to Laravel's DemoController — no API key required, no credits
// deducted. Abuse is throttled at the Lightsail proxy (3 POST/day per IP).
// The paid/biz flow lives under /api/tasks (OpenDataController) and is
// NOT what this component uses.
const API_BASE = 'https://ai-api.ge-room.com/api/demo';

// How often we poll task status (ms)
const POLL_INTERVAL = 3000;
// How often we re-render the elapsed-time progress bar (ms)
const PROGRESS_TICK = 300;
// localStorage key for persisting past generations
const HISTORY_KEY = 'ge-room-history-v1';
// Max items shown before the grid collapses into a "Show more" toggle
const HISTORY_VISIBLE = 9;

// task_type in the backend: 1 = Photo→3D, 2 = Video→3D, 3 = Photo→Painting.
// This component only exposes the two image flows.
type TaskType = 1 | 3;
type TaskStatus = 'idle' | 'uploading' | 'processing' | 'completed' | 'failed';

interface TaskResult {
  task_id: string;
  item_id?: number | string;
  name?: string;
  created_at?: number;
  task_type?: TaskType;
  img_link?: string;
  ar_link?: string;
  glb_url?: string;
  fbx_url?: string;
  usdz_url?: string;
}

// Phase 1 multi-view feature: backend returns 3 AI-generated orthographic
// views (front / left / top) alongside the task_id. Each is a CloudFront
// URL to a PNG tile. Presence of this block triggers the "AI view
// generation" showcase phase in the Result panel. Absence = the demo ran
// in legacy single-image mode (feature flag off or Gemini unavailable).
//
// `proportions` contains silhouette bounding-box pixel dimensions per tile,
// which we use to derive real L:W:H ratios for the product:
//   L:H = front.w / front.h
//   W:H = left.w  / left.h
// So typing any one dimension snaps the other two to the image's geometry.
interface GeminiViews {
  front_url?: string;
  left_url?: string;
  top_url?: string;
  composite_url?: string;
  // Optional clean line-drawing variants of the 3 views. Present only
  // when the Gemini lineart-v1 pass succeeded (added 2026-04-19). Used
  // by the DXF pipeline for dramatically cleaner vectorization than
  // the photo tiles allow. The photo tiles stay as the display source.
  front_line_url?: string;
  left_line_url?: string;
  top_line_url?: string;
  line_composite_url?: string;
  proportions?: {
    front?: { w: number; h: number };
    left?:  { w: number; h: number };
    top?:   { w: number; h: number };
  };
}

// Expected wall-clock duration for a 3D generation in seconds.
// Tuned from real pipeline traces: Tripo image_to_model (~30-120s) +
// convert_model (~5-20s) + Blender rescale (~5-30s) + S3 upload + notify.
// Planning for ~5 min so the bar tracks real time rather than undercuts it.
const EXPECTED_DURATION = 300;

// Format elapsed seconds as m:ss (e.g. 185 -> "3:05"). Under 60 seconds
// stays as "Ns" for compactness.
function formatElapsed(sec: number): string {
  const s = Math.floor(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

// Ease-in curve: slow start (lets the user feel work is happening), then
// accelerates toward the finish — the opposite of the previous ease-out,
// which decelerated right when the user was getting impatient.
// If the task runs longer than expected, gracefully asymptote toward 98%
// instead of jamming at 95% so the bar keeps crawling.
function estimateProgress(elapsedSec: number): number {
  const x = elapsedSec / EXPECTED_DURATION;
  if (x <= 1) {
    // slow-start, fast-finish: x^1.5 spends more wall time under 50%
    // and picks up speed approaching 95%.
    return 95 * Math.pow(x, 1.5);
  }
  // overtime: asymptote 95% → 98% over ~2 more minutes
  const overdue = elapsedSec - EXPECTED_DURATION;
  return 95 + 3 * (1 - Math.exp(-overdue / 60));
}

export function ImageGenerator() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [taskType, setTaskType] = useState<TaskType>(1);
  const [dimensions, setDimensions] = useState({ length: 100, width: 100, height: 50 });
  const [unit, setUnit] = useState<'cm' | 'm' | 'inch'>('cm');
  const [itemName, setItemName] = useState('');
  const [status, setStatus] = useState<TaskStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TaskResult | null>(null);
  const [progress, setProgress] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [history, setHistory] = useState<TaskResult[]>([]);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [gallery, setGallery] = useState<TaskResult[]>([]);
  // How many gallery cards have been "promoted" from static thumbnail to
  // live auto-rotating <model-viewer>. We stagger the reveal (first row
  // immediately, the rest one-by-one every ~1.4s) so N GLBs don't hammer
  // the network at the same instant. Every card eventually becomes a
  // live auto-rotating 3D viewer — no click needed.
  const [revealedGalleryCount, setRevealedGalleryCount] = useState(0);
  // Phase 1: the 3 AI-generated orthographic views returned by POST /tasks.
  // When present, the "AI view generation" strip is rendered above the
  // Tripo progress circle during the processing phase.
  const [geminiViews, setGeminiViews] = useState<GeminiViews | null>(null);
  // Phase 1: L:H and W:H ratios derived from Gemini silhouette bboxes.
  // When set, editing ANY dimension field proportionally recomputes the
  // other two so the L\u00d7W\u00d7H volume matches the image geometry. Users
  // only need to type one real-world number (e.g. chair height = 90 cm)
  // and the system fills the rest.
  const [linkedRatios, setLinkedRatios] = useState<{ lH: number; wH: number } | null>(null);
  // One-shot flag so the auto-fill only applies once (when the Gemini
  // response arrives for the first time). If the user then edits the
  // dimensions, subsequent recomputes happen via handleDimensionChange.
  const ratiosAppliedRef = useRef(false);
  // Has the user typed in ANY dimension field? If yes, the Gemini
  // auto-fill MUST NOT overwrite their values — user intent is top
  // priority. We still capture the ratios (so editing one field still
  // propagates to the others), but the initial snap-to-image is skipped.
  const dimensionsTouchedRef = useRef(false);
  // Phase-1 DXF export state: set once POST /dxf_export completes. When
  // set, the completed-view panel renders the inline SVG preview and a
  // "Download DXF" link. Not set = user hasn't clicked "Generate DXF" yet
  // (or it failed).
  const [dxfExport, setDxfExport] = useState<{ svg_url: string; dxf_url: string } | null>(null);
  const [dxfLoading, setDxfLoading] = useState(false);
  const [dxfError, setDxfError] = useState<string | null>(null);
  // Mobile-crash mitigation (2026-04-20): cap simultaneous gallery
  // <model-viewer> instances to MAX_ACTIVE_VIEWERS so we stay well below
  // the iOS Safari / Chrome iOS WebGL-context limit (8). The Hero canvas
  // already takes 1 context, so allowing only 3 here keeps total ≤ 4.
  // Cards outside the active set fall back to the static <img> poster.
  // `activeViewerIds` is recomputed whenever the IntersectionObserver
  // updates intersection ratios; the 3 most-visible cards win.
  const [activeViewerIds, setActiveViewerIds] = useState<Set<string>>(new Set());

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval>>();
  const tickRef = useRef<ReturnType<typeof setInterval>>();
  const startTimeRef = useRef<number>(0);
  const abortedRef = useRef(false);
  // Per-card DOM refs for the IntersectionObserver. Map keyed by task_id
  // so we survive gallery reorders. Set/cleared in each card's ref callback.
  const galleryCardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  // Live intersection ratio per card_id. Updated by the IO callback;
  // sorted-and-sliced by the recompute-active step. Stored in a ref
  // (not state) to avoid re-renders on every IO event — only the
  // top-3-visible *result* triggers a re-render via setActiveViewerIds.
  const galleryIntersectionRef = useRef<Map<string, number>>(new Map());
  // Hard cap for simultaneous WebGL contexts in the gallery. Tested
  // value: iOS Safari WebGL limit ≈ 8; Hero takes 1; 3 here gives a
  // 4-context budget with comfortable headroom for Tripo result panel.
  const MAX_ACTIVE_VIEWERS = 3;

  // --- History persistence + initial gallery fetch ---
  useEffect(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setHistory(parsed);
      }
    } catch {
      // Corrupt storage — start fresh.
    }
    refreshGallery();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (tickRef.current) clearInterval(tickRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pull the public community feed. Safe to call on demand — failures are
  // silent (gallery is a nice-to-have, the rest of the UI works without it).
  const refreshGallery = async () => {
    try {
      const res = await fetch(`${API_BASE}/gallery?limit=12`);
      if (!res.ok) return;
      const json = await res.json();
      if (Array.isArray(json?.data)) setGallery(json.data);
    } catch {
      /* network hiccup — leave existing gallery state alone */
    }
  };

  // Stagger the reveal of auto-rotating <model-viewer> instances. Rendering
  // 9 simultaneously would hit the network + GPU hard; instead we promote
  // the first row of 3 immediately and then reveal the remaining cards
  // one-by-one every ~1.4s. Every card ends up as a live 3D viewer — just
  // not all at the same instant.
  useEffect(() => {
    // gallery + history size drives the target count
    const total = Math.max(gallery.length, history.length);
    if (total === 0) {
      setRevealedGalleryCount(0);
      return;
    }

    // First row (3 cards) reveal immediately.
    const INITIAL = Math.min(3, total);
    setRevealedGalleryCount((prev) => Math.max(prev, INITIAL));

    if (total <= INITIAL) return;

    let current = INITIAL;
    const timer = setInterval(() => {
      current += 1;
      setRevealedGalleryCount((prev) => Math.max(prev, current));
      if (current >= total) clearInterval(timer);
    }, 1400);

    return () => clearInterval(timer);
  }, [gallery.length, history.length]);

  useEffect(() => {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
    } catch {
      // localStorage may be full / disabled — non-fatal.
    }
  }, [history]);

  // ── Mobile-crash mitigation: gate gallery <model-viewer> rendering on
  //    the IntersectionObserver. At most MAX_ACTIVE_VIEWERS cards mount
  //    a real model-viewer at any time; the rest show their static poster.
  //    Card with the highest intersection ratio wins.
  //
  //    Why: each <model-viewer> allocates a WebGL context. iOS Safari and
  //    Chrome iOS cap concurrent contexts at ~8. With 9 gallery cards +
  //    the Hero R3F canvas, prior code saturated the limit on scroll →
  //    tab crash. This change keeps the gallery's contribution at ≤ 3.
  //
  //    Re-runs when gallery+history grow so newly-added cards are observed.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      return;
    }
    const totalCards = Math.max(gallery.length, history.length);
    if (totalCards === 0) return;

    const recomputeActive = () => {
      const sorted = Array.from(galleryIntersectionRef.current.entries())
        .filter(([, ratio]) => ratio > 0)
        .sort((a, b) => b[1] - a[1])
        .slice(0, MAX_ACTIVE_VIEWERS);
      const nextSet = new Set(sorted.map(([id]) => id));
      // Only update state if the active set actually changed — avoids
      // an unnecessary re-render storm during fast scroll.
      setActiveViewerIds((prev) => {
        if (prev.size === nextSet.size && Array.from(prev).every((id) => nextSet.has(id))) {
          return prev;
        }
        return nextSet;
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = (entry.target as HTMLElement).dataset.taskId;
          if (!id) continue;
          if (entry.isIntersecting) {
            galleryIntersectionRef.current.set(id, entry.intersectionRatio);
          } else {
            galleryIntersectionRef.current.delete(id);
          }
        }
        recomputeActive();
      },
      {
        // Multiple thresholds so we get fresh ratios as cards scroll
        // through; lets the "top 3 most visible" arbitration be smooth.
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1.0],
        rootMargin: '0px',
      }
    );

    // Observe every card currently in the DOM. Refs were attached during
    // the preceding render's commit phase, so the map is populated.
    for (const el of galleryCardRefs.current.values()) {
      observer.observe(el);
    }

    return () => {
      observer.disconnect();
      galleryIntersectionRef.current.clear();
      // Don't reset activeViewerIds here — the next observer's first
      // batch of entries will populate it from scratch immediately.
    };
  }, [gallery.length, history.length]);

  const addToHistory = (item: TaskResult) => {
    setHistory(prev => {
      const next = [item, ...prev.filter(h => h.task_id !== item.task_id)];
      return next.slice(0, 50);
    });
  };

  // Remove a single item from the local history grid.
  // Doesn't hit the server — history lives only in this browser's localStorage.
  // If the deleted item happens to be the one currently shown in the viewer,
  // reset the panel back to idle so we don't keep a dangling reference.
  const removeFromHistory = (taskId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setHistory(prev => prev.filter(h => h.task_id !== taskId));
    if (result?.task_id === taskId) {
      setResult(null);
      setStatus('idle');
      setError(null);
      setProgress(0);
      setElapsed(0);
    }
  };

  // Any image type the browser can decode is accepted. Formats Tripo may
  // not handle natively (AVIF, HEIC, etc.) are transparently re-encoded
  // to PNG via canvas in `normalizeImage()` before upload.
  const MAX_BYTES = 10 * 1024 * 1024; // 10 MB

  const validateFile = (f: File): string | null => {
    if (!f.type.startsWith('image/')) {
      return `Unsupported file type (${f.type || 'unknown'}). Please upload an image.`;
    }
    if (f.size > MAX_BYTES) {
      return `File too large (${(f.size / 1024 / 1024).toFixed(1)} MB). Max 10 MB.`;
    }
    return null;
  };

  // Formats we can pass straight through to Tripo without re-encoding.
  const TRIPO_NATIVE = /^image\/(jpeg|png|webp)$/i;

  // Decode any browser-supported image (AVIF, GIF, BMP, TIFF, HEIC on Safari…)
  // and re-encode as PNG so the server pipeline only ever sees PNG/JPEG/WebP.
  // Falls back to the original file if the browser can't decode it — the
  // server-side allowed-extensions list still catches those cases.
  const normalizeImage = (f: File): Promise<File> =>
    new Promise((resolve) => {
      if (TRIPO_NATIVE.test(f.type)) return resolve(f);
      const url = URL.createObjectURL(f);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(url);
          return resolve(f);
        }
        ctx.drawImage(img, 0, 0);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);
            if (!blob) return resolve(f);
            const newName = f.name.replace(/\.[^.]+$/, '') + '.png';
            resolve(new File([blob], newName, { type: 'image/png' }));
          },
          'image/png',
          0.95
        );
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        // Browser can't decode — hand the original bytes to the server and
        // let its allowed-extensions list decide.
        resolve(f);
      };
      img.src = url;
    });

  // --- Drag & drop + file select ---
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (!droppedFile) return;
    const err = validateFile(droppedFile);
    if (err) {
      setError(err);
      return;
    }
    setFile(droppedFile);
    setPreview(URL.createObjectURL(droppedFile));
    setError(null);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    const err = validateFile(selectedFile);
    if (err) {
      setError(err);
      // Reset the input so the user can re-pick
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setFile(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
    setError(null);
  };

  // --- Elapsed-time progress bar ---
  const startProgressTicker = () => {
    startTimeRef.current = Date.now();
    setElapsed(0);
    setProgress(0);
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      const sec = (Date.now() - startTimeRef.current) / 1000;
      setElapsed(sec);
      setProgress(estimateProgress(sec));
    }, PROGRESS_TICK);
  };

  const stopProgressTicker = (finalProgress?: number) => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (typeof finalProgress === 'number') setProgress(finalProgress);
  };

  // --- Polling loop (GET /tasks/{id}) ---
  const startPolling = (taskId: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    abortedRef.current = false;

    pollRef.current = setInterval(async () => {
      if (abortedRef.current) return;
      try {
        const res = await fetch(`${API_BASE}/tasks/${taskId}`);
        if (!res.ok) throw new Error(`Status check failed: ${res.status}`);

        const data = await res.json();

        if (data.task_status === 'completed' && data.item_id) {
          clearInterval(pollRef.current);
          // Fetch item details (GET /items/{id})
          const itemRes = await fetch(`${API_BASE}/items/${data.item_id}`);
          if (!itemRes.ok) throw new Error(`Item fetch failed: ${itemRes.status}`);
          const itemJson = await itemRes.json();

          // Laravel wraps the item payload in { account_name, data: {...} }.
          // Fall back to the root object in case the API shape changes later.
          const item = itemJson?.data && typeof itemJson.data === 'object' && !Array.isArray(itemJson.data)
            ? itemJson.data
            : itemJson;

          if (!item || (!item.img_link && !item.glb_url)) {
            throw new Error('Item details returned by server are empty — generation may have failed silently.');
          }

          const completed: TaskResult = {
            task_id: taskId,
            item_id: data.item_id,
            name: itemName.trim() || `demo-${Date.now()}`,
            created_at: Date.now(),
            task_type: taskType,
            img_link: item.img_link,
            ar_link: item.ar_link,
            glb_url: item.glb_url,
            fbx_url: item.fbx_url,
            usdz_url: item.usdz_url,
          };

          setResult(completed);
          addToHistory(completed);
          stopProgressTicker(100);
          setStatus('completed');
          // The new model is now public — pull the fresh community feed so
          // the user sees their item appear in the gallery below.
          refreshGallery();
        } else if (data.task_status === 'failed') {
          clearInterval(pollRef.current);
          stopProgressTicker(0);
          setStatus('failed');
          setError(data.message || '3D generation failed');
        }
        // else still in_progress — keep polling
      } catch (err) {
        clearInterval(pollRef.current);
        stopProgressTicker(0);
        setStatus('failed');
        setError(err instanceof Error ? err.message : 'Lost connection while checking task status');
      }
    }, POLL_INTERVAL);
  };

  // --- Submit a new task (POST /tasks multipart) ---
  const handleSubmit = async () => {
    if (!file) return;

    setStatus('uploading');
    setError(null);
    setResult(null);
    startProgressTicker();

    try {
      // Transparently re-encode AVIF / GIF / TIFF / BMP / etc. to PNG so the
      // server pipeline (Laravel upload validator + Tripo) only ever sees
      // formats we know it handles. JPEG / PNG / WebP pass through as-is.
      const uploadFile = await normalizeImage(file);

      const formData = new FormData();
      formData.append('task_type', String(taskType));
      formData.append('data', JSON.stringify({
        name: itemName.trim() || `demo-${Date.now()}`,
        dimension: {
          unit,
          length: dimensions.length,
          width: dimensions.width,
          height: dimensions.height,
        },
      }));
      formData.append('file', uploadFile);

      const response = await fetch(`${API_BASE}/tasks`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.message || `API error: ${response.status}`);
      }

      const data = await response.json();
      const taskId = data.task_id || data.id;
      if (!taskId) throw new Error('No task_id returned from server');

      // Phase 1: if the backend generated 3 AI orthographic views before
      // kicking off Tripo, capture them now so the UI can showcase them
      // during the processing phase. Absence is OK — the progress circle
      // just runs by itself (legacy behavior).
      if (data.gemini_views && typeof data.gemini_views === 'object') {
        setGeminiViews(data.gemini_views);

        // Derive L:H and W:H ratios from silhouette bboxes. Front tile:
        // width = Length, height = Height. Left tile: width = Width
        // (depth), height = Height. Ratios are always captured so the
        // "locked to image — edit one, others follow" behaviour works
        // regardless of whether the initial values are user-supplied or
        // auto-filled.
        const p = data.gemini_views.proportions;
        if (p?.front?.w && p.front.h && p.left?.w && p.left.h && !ratiosAppliedRef.current) {
          const lH = p.front.w / p.front.h; // length-per-height
          const wH = p.left.w  / p.left.h;  // width-per-height
          setLinkedRatios({ lH, wH });
          // Auto-fill ONLY when the user hasn't typed anything yet. If
          // they already edited a dimension, user intent is top priority
          // and we leave their values alone (they may have measured the
          // real chair with a tape, no sense overwriting with a default).
          if (!dimensionsTouchedRef.current) {
            const maxR = Math.max(lH, wH, 1);
            const H = Math.round(100 / maxR);
            setDimensions({
              length: Math.max(1, Math.round(H * lH)),
              width:  Math.max(1, Math.round(H * wH)),
              height: Math.max(1, H),
            });
          }
          ratiosAppliedRef.current = true;
        }
      }

      setStatus('processing');
      startPolling(taskId);
    } catch (err) {
      stopProgressTicker(0);
      setError(err instanceof Error ? err.message : 'An error occurred');
      setStatus('failed');
    }
  };

  // Convert a dimension value in whatever unit the user picked into cm,
  // the canonical unit the DXF backend expects. Matches the conversion
  // the Laravel `toCentimeter()` helper does server-side for regular tasks.
  const toCm = (v: number): number => {
    if (unit === 'm') return v * 100;
    if (unit === 'inch') return v * 2.54;
    return v; // cm
  };

  // Generate the DXF + SVG technical drawing from the 3 Gemini views.
  // Only available after a 3D generation finished with `gemini_views` — the
  // button disables otherwise. Takes <~2s (mostly network).
  const handleGenerateDxf = async () => {
    if (!geminiViews?.front_url || !geminiViews.left_url || !geminiViews.top_url) {
      setDxfError('3 AI-generated views are required to build a DXF.');
      return;
    }
    setDxfLoading(true);
    setDxfError(null);
    setDxfExport(null);
    try {
      const L = toCm(dimensions.length);
      const W = toCm(dimensions.width);
      const H = toCm(dimensions.height);
      // Axis mapping:
      //   front view shows L (horizontal) × H (vertical)
      //   left  view shows W (horizontal) × H (vertical)
      //   top   view shows L (horizontal) × W (vertical)
      // When the Gemini lineart pass is available, forward those URLs as
      // `line_url` — the backend vectorizer will prefer them over the
      // photo tiles for a much cleaner DXF. Photo `url` is still passed
      // as a fallback in case the line-art download fails server-side.
      const payload = {
        views: [
          {
            url: geminiViews.front_url,
            line_url: geminiViews.front_line_url,
            label: 'FRONT',
            width_cm: L,
            height_cm: H,
          },
          {
            url: geminiViews.left_url,
            line_url: geminiViews.left_line_url,
            label: 'LEFT',
            width_cm: W,
            height_cm: H,
          },
          {
            url: geminiViews.top_url,
            line_url: geminiViews.top_line_url,
            label: 'TOP',
            width_cm: L,
            height_cm: W,
          },
        ],
        product_name: (result?.name || itemName || 'product').toString(),
      };
      const res = await fetch(`${API_BASE}/dxf_export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.message || `API error: ${res.status}`);
      }
      const data = await res.json();
      if (!data.dxf_url || !data.svg_url) {
        throw new Error('DXF response missing URL');
      }
      setDxfExport({ svg_url: data.svg_url, dxf_url: data.dxf_url });
    } catch (e) {
      setDxfError(e instanceof Error ? e.message : 'DXF generation failed');
    } finally {
      setDxfLoading(false);
    }
  };

  // Smart dimension update:
  //  - If we have linked ratios from Gemini, editing any field recomputes
  //    the other two proportionally — typing height=90 snaps length+width
  //    to the silhouette-correct values.
  //  - If no ratios (Gemini disabled / failed), behave like a plain input.
  // We always keep the typed value exact — only the OTHER fields round,
  // so the user never fights the rounding on the cell they're editing.
  const handleDimensionChange = (field: 'length' | 'width' | 'height', raw: string) => {
    const value = Number(raw);
    // Flag as "user-touched" so a later Gemini response won't stomp on
    // what they typed. One-way flag; the session stays user-priority
    // until handleReset clears it.
    dimensionsTouchedRef.current = true;
    if (!linkedRatios || !Number.isFinite(value) || value <= 0) {
      setDimensions((d) => ({ ...d, [field]: value }));
      return;
    }
    const { lH, wH } = linkedRatios;
    let H: number;
    if (field === 'height') H = value;
    else if (field === 'length') H = value / lH;
    else /* width */ H = value / wH;
    setDimensions({
      length: field === 'length' ? value : Math.max(1, Math.round(H * lH)),
      width:  field === 'width'  ? value : Math.max(1, Math.round(H * wH)),
      height: field === 'height' ? value : Math.max(1, Math.round(H)),
    });
  };

  const handleReset = () => {
    abortedRef.current = true;
    if (pollRef.current) clearInterval(pollRef.current);
    if (tickRef.current) clearInterval(tickRef.current);
    setFile(null);
    setPreview(null);
    setStatus('idle');
    setError(null);
    setResult(null);
    setProgress(0);
    setElapsed(0);
    setItemName('');
    setGeminiViews(null);
    setLinkedRatios(null);
    setDimensions({ length: 100, width: 100, height: 50 });
    ratiosAppliedRef.current = false;
    dimensionsTouchedRef.current = false;
    setDxfExport(null);
    setDxfError(null);
    setDxfLoading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const isProcessing = status === 'uploading' || status === 'processing';

  // Build the community gallery:
  //  1. Start with server items (public, completed, latest 12).
  //  2. Prepend any locally-generated items (from this browser's history)
  //     that the server hasn't picked up yet — this covers the brief window
  //     after a brand-new generation completes and before refreshGallery()
  //     propagates it.
  //  3. Tag each item with `mine` if the current browser has it in history
  //     — only those items show the delete button + "Yours" badge.
  const galleryItemIds = new Set(gallery.map(g => g.item_id));
  const myIds = new Set(history.map(h => h.item_id));
  const myRecentNotInGallery = history.filter(h => h.item_id && !galleryItemIds.has(h.item_id));
  const mergedGallery = [...myRecentNotInGallery, ...gallery].map(item => ({
    ...item,
    mine: item.item_id != null && myIds.has(item.item_id),
  }));
  const visibleHistory = showAllHistory ? mergedGallery : mergedGallery.slice(0, HISTORY_VISIBLE);
  const hiddenCount = Math.max(0, mergedGallery.length - HISTORY_VISIBLE);

  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4 tracking-tight">
            Try It Now
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Upload an image and let our AI transform it into a 3D model or artistic painting
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Upload & Settings */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100"
          >
            {/* Task Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">
                Generation Type
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setTaskType(1)}
                  disabled={isProcessing}
                  className={clsx(
                    'flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all disabled:opacity-50',
                    taskType === 1
                      ? 'border-black bg-black text-white'
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <Box className="w-5 h-5" />
                  <span className="font-medium">3D Model</span>
                </button>
                <button
                  onClick={() => setTaskType(3)}
                  disabled={isProcessing}
                  className={clsx(
                    'flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all disabled:opacity-50',
                    taskType === 3
                      ? 'border-black bg-black text-white'
                      : 'border-gray-200 hover:border-gray-300'
                  )}
                >
                  <Image className="w-5 h-5" />
                  <span className="font-medium">Painting</span>
                </button>
              </div>
            </div>

            {/* File Upload */}
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-3 uppercase tracking-wider">
                Upload Image
              </label>
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
                className={clsx(
                  'relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all',
                  preview
                    ? 'border-green-400 bg-green-50'
                    : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                )}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {preview ? (
                  <div className="relative">
                    <img
                      src={preview}
                      alt="Preview"
                      className="max-h-48 mx-auto rounded-lg object-contain"
                    />
                    <p className="mt-3 text-sm text-gray-600">{file?.name}</p>
                  </div>
                ) : (
                  <div className="py-8">
                    <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600 font-medium">
                      Drag & drop or click to upload
                    </p>
                    <p className="text-sm text-gray-400 mt-2">
                      Any image — PNG, JPEG, WebP, AVIF, HEIC, GIF… (max 10 MB)
                    </p>
                  </div>
                )}
              </div>
              {/* Upload-level error (unsupported format, file too big) —
                  shown inline so the user sees it before they click Generate. */}
              {error && status === 'idle' && (
                <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                  <XCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </p>
              )}
            </div>

            {/* Item Name */}
            <div className="mb-6">
              <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                Name (Optional)
              </label>
              <input
                type="text"
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                placeholder="My Product"
                disabled={isProcessing}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black disabled:opacity-50"
              />
            </div>

            {/* Dimensions */}
            <div className="mb-8">
              <div className="flex items-end justify-between mb-3">
                <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">
                  Dimensions
                </label>
                {linkedRatios && (
                  <span className="text-[11px] text-blue-600 font-medium inline-flex items-center gap-1">
                    <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.464 3.464a5 5 0 017.072 7.072l-3 3a5 5 0 01-7.072 0 1 1 0 011.415-1.415 3 3 0 004.242 0l3-3a3 3 0 10-4.243-4.243l-1.414 1.415a1 1 0 11-1.415-1.415l1.415-1.414zM4.465 9.464a1 1 0 011.414 0 3 3 0 010 4.243l-1.415 1.414a3 3 0 004.243 4.243l3-3a3 3 0 000-4.243 1 1 0 111.414-1.414 5 5 0 010 7.071l-3 3a5 5 0 01-7.071-7.071l1.415-1.414a1 1 0 010-1.415z" clipRule="evenodd" />
                    </svg>
                    Locked to image — edit one, others follow
                  </span>
                )}
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">
                    {taskType === 3 ? 'Width' : 'Length'}
                  </label>
                  <input
                    type="number"
                    value={dimensions.length}
                    onChange={(e) => handleDimensionChange('length', e.target.value)}
                    disabled={isProcessing}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">
                    {taskType === 3 ? 'Height' : 'Width'}
                  </label>
                  <input
                    type="number"
                    value={dimensions.width}
                    onChange={(e) => handleDimensionChange('width', e.target.value)}
                    disabled={isProcessing}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">
                    {taskType === 3 ? 'Thickness' : 'Height'}
                  </label>
                  <input
                    type="number"
                    value={dimensions.height}
                    onChange={(e) => handleDimensionChange('height', e.target.value)}
                    disabled={isProcessing}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black disabled:opacity-50"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Unit</label>
                  <select
                    value={unit}
                    onChange={(e) => setUnit(e.target.value as 'cm' | 'm' | 'inch')}
                    disabled={isProcessing}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black disabled:opacity-50"
                  >
                    <option value="cm">cm</option>
                    <option value="m">m</option>
                    <option value="inch">inch</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={!file || isProcessing}
              className={clsx(
                'w-full py-4 rounded-xl font-bold text-sm uppercase tracking-wider transition-all flex items-center justify-center gap-2',
                !file || isProcessing
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-black text-white hover:bg-gray-800'
              )}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>Generate {taskType === 1 ? '3D Model' : 'Painting'}</>
              )}
            </button>
          </motion.div>

          {/* Right: Result */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 flex flex-col"
          >
            <h3 className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-wider">
              Result
            </h3>

            <div className="flex-1 flex items-center justify-center min-h-[420px]">
              <AnimatePresence mode="wait">
                {status === 'idle' && (
                  <motion.div
                    key="idle"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center text-gray-400"
                  >
                    <Box className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>Upload an image to get started</p>
                  </motion.div>
                )}

                {isProcessing && (
                  <motion.div
                    key="processing"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center w-full"
                  >
                    {/* ── Phase 1: AI-generated views strip ──────────────────
                         Rendered during upload (skeleton shimmer in each slot)
                         and stays visible through the Tripo phase (once the
                         3 URLs arrive from POST /tasks). Collapses cleanly
                         when the backend returned no gemini_views — we then
                         fall back to the legacy progress-circle-only layout. */}
                    {(status === 'uploading' || geminiViews) && (
                      <div className="mb-6">
                        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-[0.12em] mb-3">
                          {geminiViews
                            ? 'AI-generated orthographic views'
                            : 'Generating views with AI...'}
                        </p>
                        <div className="grid grid-cols-3 gap-2 max-w-md mx-auto">
                          {(['front', 'left', 'top'] as const).map((key, idx) => {
                            const url = geminiViews?.[`${key}_url` as const];
                            return (
                              <div
                                key={key}
                                className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100"
                              >
                                {url ? (
                                  <motion.img
                                    initial={{ opacity: 0, scale: 0.92 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.45, delay: idx * 0.12 }}
                                    src={url}
                                    alt={`${key} view`}
                                    className="absolute inset-0 w-full h-full object-contain"
                                  />
                                ) : (
                                  // Skeleton shimmer — sweeps left-to-right every 1.4s
                                  <div
                                    className="absolute inset-0 bg-[linear-gradient(110deg,rgba(240,244,255,0)_0%,rgba(75,92,219,0.08)_45%,rgba(75,92,219,0.20)_50%,rgba(75,92,219,0.08)_55%,rgba(240,244,255,0)_100%)] bg-[length:200%_100%] animate-shimmer"
                                  />
                                )}
                                <div className="absolute inset-x-0 bottom-0 bg-black/55 text-white text-[9px] font-semibold uppercase tracking-[0.12em] text-center py-0.5">
                                  {key}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Circular progress — driven by elapsed time, snaps to 100% on completion */}
                    <div className="relative w-32 h-32 mx-auto mb-6">
                      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 96 96">
                        <circle
                          cx="48"
                          cy="48"
                          r="40"
                          stroke="#e5e7eb"
                          strokeWidth="8"
                          fill="none"
                        />
                        <circle
                          cx="48"
                          cy="48"
                          r="40"
                          stroke="#000"
                          strokeWidth="8"
                          fill="none"
                          strokeDasharray={251.2}
                          strokeDashoffset={251.2 - (251.2 * progress) / 100}
                          strokeLinecap="round"
                          className="transition-all duration-300 ease-out"
                        />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center font-bold text-xl">
                        {Math.round(progress)}%
                      </span>
                    </div>
                    <p className="text-gray-600 font-medium">
                      {status === 'uploading'
                        ? 'Generating views with AI...'
                        : geminiViews
                          ? 'Building 3D model from 3 views...'
                          : 'AI is working its magic...'}
                    </p>
                    <p className="text-sm text-gray-400 mt-2">
                      {formatElapsed(elapsed)} elapsed · typically 3–5 min
                    </p>
                  </motion.div>
                )}

                {status === 'completed' && result && (
                  <motion.div
                    key="completed"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    className="w-full"
                  >
                    {/* 3D viewer for models, static image for paintings */}
                    {result.glb_url ? (
                      <div className="w-full rounded-xl overflow-hidden border border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 mb-4">
                        <model-viewer
                          src={result.glb_url}
                          ios-src={result.usdz_url}
                          alt={result.name || '3D model'}
                          poster={result.img_link}
                          auto-rotate
                          camera-controls
                          ar
                          ar-modes="webxr scene-viewer quick-look"
                          shadow-intensity="1"
                          exposure="1"
                          style={{ width: '100%', height: 320, background: 'transparent' }}
                        />
                      </div>
                    ) : result.img_link ? (
                      <img
                        src={result.img_link}
                        alt={result.name || 'Generated result'}
                        className="w-full max-h-80 object-contain rounded-xl mb-4 border border-gray-200"
                      />
                    ) : null}

                    <div className="flex items-center gap-2 mb-4 text-green-700">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-bold">Generation complete</span>
                    </div>

                    {/* ── Phase 1: AI source views recap (under the model) ──
                         Shows the 3 orthographic views that produced the 3D
                         model — closes the loop visually so the user sees
                         exactly what the AI built from. */}
                    {geminiViews && (
                      <div className="mb-4 pt-4 border-t border-gray-100">
                        <p className="text-[11px] font-bold text-gray-500 uppercase tracking-[0.12em] mb-2">
                          AI-generated source views
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                          {(['front', 'left', 'top'] as const).map((key) => {
                            const url = geminiViews[`${key}_url` as const];
                            return (
                              <a
                                key={key}
                                href={url || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 block hover:border-gray-400 transition-colors"
                              >
                                {url ? (
                                  <img
                                    src={url}
                                    alt={`${key} view`}
                                    className="absolute inset-0 w-full h-full object-contain"
                                  />
                                ) : null}
                                <div className="absolute inset-x-0 bottom-0 bg-black/55 text-white text-[9px] font-semibold uppercase tracking-[0.12em] text-center py-0.5">
                                  {key}
                                </div>
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Download / AR links */}
                    <div className="space-y-2">
                      {result.ar_link && (
                        <a
                          href={result.ar_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 w-full py-3 bg-black text-white rounded-xl font-medium hover:bg-gray-800 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          View in AR
                        </a>
                      )}
                      <div className="grid grid-cols-3 gap-2">
                        {result.glb_url && (
                          <a
                            href={result.glb_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-1 py-2 border-2 border-gray-200 text-gray-700 rounded-lg text-xs font-semibold hover:bg-gray-50 transition-colors"
                          >
                            <Download className="w-3 h-3" /> GLB
                          </a>
                        )}
                        {result.fbx_url && (
                          <a
                            href={result.fbx_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-1 py-2 border-2 border-gray-200 text-gray-700 rounded-lg text-xs font-semibold hover:bg-gray-50 transition-colors"
                          >
                            <Download className="w-3 h-3" /> FBX
                          </a>
                        )}
                        {result.usdz_url && (
                          <a
                            href={result.usdz_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-1 py-2 border-2 border-gray-200 text-gray-700 rounded-lg text-xs font-semibold hover:bg-gray-50 transition-colors"
                          >
                            <Download className="w-3 h-3" /> USDZ
                          </a>
                        )}
                      </div>
                    </div>

                    {/* ── Phase 1: Technical DXF drawing generation ──────
                         Available only when we have the 3 Gemini views.
                         First click → POST /dxf_export → inline SVG preview
                         + "Download DXF" link. The SVG is served from the
                         same CDN as the other assets. */}
                    {geminiViews && taskType === 1 && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        {!dxfExport && !dxfLoading && (
                          <button
                            onClick={handleGenerateDxf}
                            className="w-full py-3 border-2 border-black text-black rounded-xl font-semibold text-sm inline-flex items-center justify-center gap-2 hover:bg-black hover:text-white transition-colors"
                          >
                            <FileCode2 className="w-4 h-4" />
                            Generate technical drawing (DXF)
                          </button>
                        )}
                        {dxfLoading && (
                          <div className="w-full py-3 border-2 border-gray-200 text-gray-500 rounded-xl text-sm inline-flex items-center justify-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Tracing contours & writing DXF…
                          </div>
                        )}
                        {dxfError && !dxfLoading && (
                          <div className="text-red-600 text-xs bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-2">
                            {dxfError}
                          </div>
                        )}
                        {dxfExport && (
                          <div>
                            <p className="text-[11px] font-bold text-gray-500 uppercase tracking-[0.12em] mb-2">
                              Technical drawing preview
                            </p>
                            <div className="relative w-full rounded-lg overflow-hidden border border-gray-200 bg-[#fafafa] mb-3">
                              {/* Inline SVG preview — fetched as-is from the
                                  CDN. <object> picks the svg width from the
                                  server response; we clamp height for layout. */}
                              <img
                                src={dxfExport.svg_url}
                                alt="Technical drawing preview"
                                className="w-full h-auto block"
                                style={{ maxHeight: 360, objectFit: 'contain' }}
                              />
                            </div>
                            <a
                              href={dxfExport.dxf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                              className="flex items-center justify-center gap-2 w-full py-3 bg-black text-white rounded-xl font-semibold hover:bg-gray-800 transition-colors"
                            >
                              <Download className="w-4 h-4" />
                              Download DXF
                            </a>
                          </div>
                        )}
                      </div>
                    )}

                    <button
                      onClick={handleReset}
                      className="mt-6 flex items-center justify-center gap-2 text-gray-500 hover:text-gray-700 mx-auto text-sm"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Try Another
                    </button>
                  </motion.div>
                )}

                {status === 'failed' && (
                  <motion.div
                    key="failed"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center"
                  >
                    <div className="bg-red-50 rounded-xl p-6 mb-6">
                      <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                      <p className="text-red-700 font-bold">Generation Failed</p>
                      <p className="text-red-600 text-sm mt-2">{error}</p>
                    </div>
                    <button
                      onClick={handleReset}
                      className="flex items-center justify-center gap-2 text-gray-500 hover:text-gray-700 mx-auto"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Try Again
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>

        {/* ============ Community gallery (3×3, collapsible overflow) ============
             Pulls the latest public models from the server every mount and
             after each successful generation. Items the current browser
             produced get a "Yours" badge + delete control. */}
        {mergedGallery.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16"
          >
            <div className="flex items-end justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-black tracking-tight">
                  Latest community generations
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Each model auto-rotates — drag to spin manually
                </p>
              </div>
              <span className="text-xs text-gray-500 uppercase tracking-wider">
                {mergedGallery.length} {mergedGallery.length === 1 ? 'item' : 'items'}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {visibleHistory.map((item, idx) => {
                // Cards are revealed left-to-right, top-to-bottom. First 3
                // cards (the visible first row) reveal at mount; each
                // subsequent index unlocks every ~1.4s (see the stagger
                // effect near refreshGallery). Until revealed, show the
                // lightweight <img> poster.
                //
                // Mobile-crash mitigation (2026-04-20): on top of the
                // stagger gate, the IntersectionObserver-driven
                // activeViewerIds caps simultaneous <model-viewer>
                // instances at MAX_ACTIVE_VIEWERS. Cards outside that
                // set show their poster — so scroll-induced WebGL
                // context exhaustion can't happen.
                const staggerOk = idx < revealedGalleryCount && !!item.glb_url;
                const inActiveSet = activeViewerIds.has(item.task_id);
                const isRevealed = staggerOk && inActiveSet;

                return (
                  <div
                    key={item.task_id}
                    data-task-id={item.task_id}
                    ref={(el) => {
                      if (el) {
                        galleryCardRefs.current.set(item.task_id, el);
                      } else {
                        galleryCardRefs.current.delete(item.task_id);
                      }
                    }}
                    className="group relative aspect-square rounded-xl overflow-hidden border-2 border-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 hover:border-gray-400 hover:shadow-md transition-all"
                  >
                    {isRevealed ? (
                      <model-viewer
                        src={item.glb_url}
                        ios-src={item.usdz_url}
                        alt={item.name || '3D model'}
                        poster={item.img_link}
                        auto-rotate
                        auto-rotate-delay="0"
                        rotation-per-second="24deg"
                        camera-controls
                        disable-zoom
                        disable-pan
                        interaction-prompt="none"
                        shadow-intensity="0.4"
                        exposure="1"
                        loading="eager"
                        reveal="auto"
                        style={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          background: 'transparent',
                          '--poster-color': 'transparent',
                        } as React.CSSProperties}
                      />
                    ) : item.img_link ? (
                      <img
                        src={item.img_link}
                        alt={item.name || '3D model'}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-gray-300">
                        <Box className="w-10 h-10" />
                      </div>
                    )}

                    {/* Name label — always at the bottom */}
                    <div className="absolute inset-x-0 bottom-0 p-3 pointer-events-none bg-gradient-to-t from-black/70 to-transparent">
                      <p className="text-white text-xs font-semibold truncate">
                        {item.name || 'Untitled'}
                      </p>
                    </div>

                    {/* "Yours" badge (top-left) */}
                    {item.mine && (
                      <div className="absolute top-2 left-2 bg-blue-600 text-white text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold pointer-events-none z-10">
                        Yours
                      </div>
                    )}

                    {/* Delete button — only on "mine" items; hover-revealed. */}
                    {item.mine && (
                      <span
                        role="button"
                        aria-label={`Remove ${item.name || 'item'} from your history`}
                        onClick={(e) => removeFromHistory(item.task_id, e)}
                        className={clsx(
                          'absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full z-10',
                          'bg-white/95 hover:bg-red-500 hover:text-white text-gray-700 shadow',
                          'opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity',
                          'cursor-pointer'
                        )}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {hiddenCount > 0 && (
              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => setShowAllHistory(v => !v)}
                  className="inline-flex items-center gap-1.5 px-5 py-2 border border-gray-300 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {showAllHistory ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Show less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Show {hiddenCount} more
                    </>
                  )}
                </button>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </section>
  );
}
